import { TFunction } from 'i18next';
import { Control, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddressModel, getFormattedAddress } from '@steelbuy/ts-shared';
import { Select } from '../primitive/select/Select';
import './DeliveryAddressSelection.scss';

interface DeliveryAddressSelectionProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
    name: TName;
    control: Control<TFieldValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rules?: any;
    error?: FieldError;
    addresses: AddressModel[];
}

export const formatAddressLabel = (address: AddressModel | undefined, t: TFunction): string => {
    if (!address) return '';
    const parts = getFormattedAddress({
        name: address.name,
        addressLine1: address.addressLine1,
        addressLine2: address.addressLine2,
        city: address.city,
        postalCode: address.postalCode,
        country: address.country,
        state: address.state,
    }).map(({ value, isEnum }) => (isEnum ? t(`domainModel:${value}` as Parameters<typeof t>[0]) : value));
    return parts.join(', ');
};

export const DeliveryAddressSelection = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    name,
    control,
    rules,
    error,
    addresses,
}: DeliveryAddressSelectionProps<TFieldValues, TName>) => {
    const { t } = useTranslation(['uiDomain', 'domainModel']);

    return (
        <div className="delivery-address-selection">
            <Select
                options={addresses.map((address) => ({
                    label: formatAddressLabel(address, t as TFunction),
                    value: address.id,
                }))}
                placeholder={t('uiDomain:materialInput.placeholder')}
                name={name}
                control={control}
                rules={rules}
                error={error?.message}
                disabled={addresses.length === 0}
            />
        </div>
    );
};
